import { getTokenNode } from '@/store/utils/tokenNode'

/** localStorage key for push-inbox client cache (per username). */
export const PUSH_INBOX_LOCAL_KEY = 'janoo_push_inbox_local'

/**
 * @typedef {{ username: string|null, newest_message_at: string|null, read_ids: number[] }} PushInboxLocalState
 */

/**
 * @returns {PushInboxLocalState}
 */
export function loadPushInboxLocalState() {
  try {
    const raw = localStorage.getItem(PUSH_INBOX_LOCAL_KEY)
    if (!raw) return { username: null, newest_message_at: null, read_ids: [] }
    const parsed = JSON.parse(raw)
    const readIds = Array.isArray(parsed && parsed.read_ids)
      ? parsed.read_ids.map((id) => Number(id)).filter((id) => Number.isFinite(id))
      : []
    return {
      username: (parsed && parsed.username) || null,
      newest_message_at: (parsed && parsed.newest_message_at) || null,
      read_ids: readIds,
    }
  } catch (e) {
    return { username: null, newest_message_at: null, read_ids: [] }
  }
}

/**
 * @param {PushInboxLocalState} state
 */
export function savePushInboxLocalState(state) {
  try {
    localStorage.setItem(
      PUSH_INBOX_LOCAL_KEY,
      JSON.stringify({
        username: (state && state.username) || null,
        newest_message_at: (state && state.newest_message_at) || null,
        read_ids: Array.isArray(state && state.read_ids) ? state.read_ids : [],
      }),
    )
  } catch (e) {
    /* quota / private mode */
  }
}

/**
 * Epoch ms for comparison; returns NaN if unusable.
 * @param {unknown} value
 * @returns {number}
 */
export function pushInboxTimestampMs(value) {
  if (value == null || value === '') return Number.NaN
  if (typeof value === 'number' && Number.isFinite(value)) return value
  const raw = String(value).trim()
  if (!raw) return Number.NaN
  const normalized = raw.includes('T') ? raw : raw.replace(' ', 'T')
  const withZone =
    /Z$|[+-]\d{2}:?\d{2}$/.test(normalized) ? normalized : `${normalized}Z`
  const ms = Date.parse(withZone)
  return Number.isNaN(ms) ? Number.NaN : ms
}

/**
 * Picks the newest timestamp string among candidates (API-ish strings).
 * @param {...unknown} candidates
 * @returns {string|null}
 */
export function pickNewestPushInboxTimestamp(...candidates) {
  let best = null
  let bestMs = Number.NEGATIVE_INFINITY
  for (const value of candidates) {
    if (value == null || value === '') continue
    const ms = pushInboxTimestampMs(value)
    if (Number.isNaN(ms)) continue
    if (ms >= bestMs) {
      bestMs = ms
      best = String(value)
    }
  }
  return best
}

/**
 * Timestamps present on a push-inbox WS payload.
 * @param {object|null|undefined} msg
 * @returns {unknown[]}
 */
export function collectPushInboxWsTimestamps(msg) {
  if (!msg || typeof msg !== 'object') return []
  const item = msg.item && typeof msg.item === 'object' ? msg.item : null
  return [
    msg.newest_message_at,
    msg.send_date,
    msg.create_date,
    msg.read_at,
    item && item.send_date,
    item && item.create_date,
    item && item.read_at,
  ]
}

/**
 * Applies a WS inbox event to localStorage:
 * - always bumps newest_message_at when the payload has a newer stamp
 * - inbox.read → add id to read_ids
 * - inbox.unread → remove id from read_ids
 *
 * @param {object} msg
 * @param {{ username?: string|null }} [opts]
 * @returns {{ state: PushInboxLocalState, cursorAdvanced: boolean }}
 */
export function persistPushInboxWsLocal(msg, opts = {}) {
  const state = loadPushInboxLocalState()
  const prevCursor = state.newest_message_at
  const username = (opts && opts.username) || state.username || null
  if (username && state.username && username !== state.username) {
    state.read_ids = []
    state.newest_message_at = null
  }
  state.username = username

  const newestFromPayload = pickNewestPushInboxTimestamp(
    ...collectPushInboxWsTimestamps(msg),
  )
  const mergedNewest = pickNewestPushInboxTimestamp(
    state.newest_message_at,
    newestFromPayload,
  )
  if (mergedNewest) state.newest_message_at = mergedNewest

  const id = msg && msg.id != null ? Number(msg.id) : Number.NaN
  if (msg && msg.type === 'inbox.read' && Number.isFinite(id)) {
    if (!state.read_ids.includes(id)) state.read_ids.push(id)
  }
  if (msg && msg.type === 'inbox.unread' && Number.isFinite(id)) {
    state.read_ids = state.read_ids.filter((x) => x !== id)
  }
  // New notification item id is not "read"
  if (msg && msg.type === 'inbox.notification' && msg.item && msg.item.id != null) {
    const itemId = Number(msg.item.id)
    if (Number.isFinite(itemId) && msg.item.read === true) {
      if (!state.read_ids.includes(itemId)) state.read_ids.push(itemId)
    }
  }

  savePushInboxLocalState(state)
  const cursorAdvanced = didPushInboxCursorAdvance(prevCursor, mergedNewest)
  return { state, cursorAdvanced }
}

/**
 * Cursor local (newest_message_at) para ?since= no próximo connect.
 * Sem cursor → omite since; API faz catch-up das últimas 24h **unread-only**.
 *
 * @returns {string|null}
 */
export function getPushInboxSinceCursor() {
  const state = loadPushInboxLocalState()
  const stamp = state && state.newest_message_at
  if (stamp == null || String(stamp).trim() === '') return null
  return String(stamp).trim()
}

/**
 * Atualiza newest_message_at a partir de itens (lista REST / inbox.sync).
 *
 * @param {Array<{ send_date?: string|null, create_date?: string|null, read_at?: string|null }>} items
 * @param {{ username?: string|null }} [opts]
 * @returns {{ state: PushInboxLocalState, cursorAdvanced: boolean }}
 */
export function bumpPushInboxCursorFromItems(items, opts = {}) {
  const state = loadPushInboxLocalState()
  const prevCursor = state.newest_message_at
  const username = (opts && opts.username) || state.username || null
  if (username && state.username && username !== state.username) {
    state.read_ids = []
    state.newest_message_at = null
  }
  state.username = username
  const stamps = []
  if (Array.isArray(items)) {
    for (const row of items) {
      if (!row) continue
      stamps.push(row.send_date, row.create_date, row.read_at)
    }
  }
  const merged = pickNewestPushInboxTimestamp(state.newest_message_at, ...stamps)
  if (merged) state.newest_message_at = merged
  savePushInboxLocalState(state)
  const cursorAdvanced = didPushInboxCursorAdvance(prevCursor, merged)
  return { state, cursorAdvanced }
}

/**
 * Título do card/notify: title → model_name → fallback.
 *
 * @param {object|null|undefined} item
 * @returns {string}
 */
export function pushInboxItemTitle(item) {
  return (item && (item.title || item.model_name)) || 'Notificação'
}

/**
 * Cursor avançou: primeira gravação (sem stamp anterior) ou stamp mais novo.
 *
 * @param {unknown} prevCursor
 * @param {unknown} nextCursor
 * @returns {boolean}
 */
export function didPushInboxCursorAdvance(prevCursor, nextCursor) {
  if (nextCursor == null || String(nextCursor).trim() === '') return false
  const nextMs = pushInboxTimestampMs(nextCursor)
  if (Number.isNaN(nextMs)) return false
  const prevMs = pushInboxTimestampMs(prevCursor)
  if (Number.isNaN(prevMs)) return true
  return nextMs > prevMs
}

/**
 * Desktop Notification com o total de não lidas (N).
 * Pede permission se ainda estiver `default`.
 *
 * @param {number} unreadCount
 * @returns {Notification|null}
 */
export function notifyPushInboxDesktop(unreadCount) {
  const n = Number(unreadCount) || 0
  if (n <= 0) return null
  if (typeof window === 'undefined' || typeof Notification === 'undefined') {
    return null
  }
  const title = 'Janoo'
  const body =
    n === 1 ? 'Você tem 1 nova mensagem' : `Você tem ${n} novas mensagens`
  const show = () => {
    try {
      return new Notification(title, {
        body,
        tag: 'janoo-push-inbox',
        renotify: true,
      })
    } catch (e) {
      return null
    }
  }
  if (Notification.permission === 'granted') return show()
  if (Notification.permission === 'denied') return null
  Notification.requestPermission().then((perm) => {
    if (perm === 'granted') show()
  })
  return null
}

/**
 * URL do WebSocket dedicado ao inbox push (/ws/push-inbox).
 * Deriva do host de VUE_APP_API_NODE_URL_V3 — não usa o WS Pix.
 *
 * Inclui ?since=<cursor> só quando há last-msg time local.
 * Sem cursor: omite since → API `inbox.sync` = últimas 24h unread-only
 * (`unread_only: true`). Com cursor: msgs após since (lidas + não lidas).
 *
 * @returns {string}
 */
export function buildPushInboxWsUrl() {
  const api = String(process.env.VUE_APP_API_NODE_URL_V3 || '')
  let base = api
  try {
    const abs = api.startsWith('http')
      ? api
      : `${window.location.protocol}${api.startsWith('//') ? '' : '//'}${api}`
    const u = new URL(abs)
    const wsProto = u.protocol === 'https:' ? 'wss:' : 'ws:'
    base = `${wsProto}//${u.host}`
  } catch (e) {
    base = 'ws://127.0.0.1:3001'
  }
  const token = getTokenNode() || ''
  const params = new URLSearchParams()
  params.set('token', token)
  const since = getPushInboxSinceCursor()
  if (since) params.set('since', since)
  return `${base}/ws/push-inbox?${params.toString()}`
}

/**
 * Normaliza o envelope `message` do item do inbox para objeto.
 *
 * @param {object|string|null} message
 * @returns {object|null}
 */
export function parsePushMessageEnvelope(message) {
  if (message == null) return null
  if (typeof message === 'string') {
    try {
      const parsed = JSON.parse(message)
      return parsePushMessageEnvelope(parsed)
    } catch (e) {
      return null
    }
  }
  if (typeof message !== 'object' || Array.isArray(message)) return null
  return message
}

/**
 * Campos de `model_message` como { name, value } para o painel expandido.
 * Ignora metadados de leitura do inbox.
 *
 * @param {object|string|null} message
 * @returns {{ name: string, value: string }[]}
 */
export function extractModelMessageFields(message) {
  const envelope = parsePushMessageEnvelope(message)
  if (!envelope) return []
  let modelMessage = envelope.model_message
  if (typeof modelMessage === 'string') {
    try {
      modelMessage = JSON.parse(modelMessage)
    } catch (e) {
      return modelMessage
        ? [{ name: 'message', value: String(modelMessage) }]
        : []
    }
  }
  if (!modelMessage || typeof modelMessage !== 'object' || Array.isArray(modelMessage)) {
    return []
  }
  const skip = new Set(['inbox_read_at'])
  return Object.keys(modelMessage)
    .filter((key) => !skip.has(key))
    .map((name) => {
      const raw = modelMessage[name]
      let value = ''
      if (raw == null) {
        value = ''
      } else if (typeof raw === 'object') {
        try {
          value = JSON.stringify(raw)
        } catch (e) {
          value = String(raw)
        }
      } else {
        value = String(raw)
      }
      return { name, value }
    })
}

/**
 * Extrai HTML/texto do envelope message do item do inbox.
 *
 * @param {object|string|null} message
 * @returns {string}
 */
export function extractPushBody(message) {
  if (message == null) return ''
  if (typeof message === 'string') {
    try {
      const parsed = JSON.parse(message)
      return extractPushBody(parsed)
    } catch (e) {
      return message
    }
  }
  if (typeof message !== 'object') return String(message)
  const nested = message.model_message
  if (nested && typeof nested === 'object' && nested.message != null) {
    return String(nested.message)
  }
  if (nested && typeof nested === 'object' && nested.caption != null) {
    return String(nested.caption)
  }
  if (message.message != null && typeof message.message !== 'object') {
    return String(message.message)
  }
  if (typeof message.message === 'object' && message.message && message.message.message != null) {
    return String(message.message.message)
  }
  return ''
}
